import { useCentralApi, useCurrentUser } from "hooks/shared";
import { IMetadataOwner, IMetadataTab } from "models/metadata";
import ITaskRequirement from "models/tasks/ITaskRequirements";
import { useEffect, useMemo, useState } from "react";


type UseWorkItemMetadataRequirementsResult = [
    ITaskRequirement[],
    boolean
];

type UseWorkItemMetadataRequirementsSignature = (owner: IMetadataOwner, refreshKey: number, draft: boolean | undefined) => UseWorkItemMetadataRequirementsResult;

const useWorkItemMetadataRequirements: UseWorkItemMetadataRequirementsSignature = (owner: IMetadataOwner, refreshKey: number, draft: boolean | undefined) => {
    
    const [ result, setResult ] = useState<ITaskRequirement[]>([]);
    const [ loading, setLoading ] = useState<boolean>(false);

    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    const { taskId, workItemId } = owner;
    const url = useMemo(() => {
        return `/api/v3/drafts/workitems/${workItemId}/metadata/requirements`;
    }, [ taskId, workItemId, refreshKey ]);

    useEffect(() => {
        const controller = new AbortController();

        if (!workItemId || !draft) {
            return;
        }

        (async () => {
            setLoading(true);
            try {
                const resp = await fetcher(url, {
                    signal: controller.signal
                });

                const data = await resp.json() as ITaskRequirement[];
                setResult(data);
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        })();

        return () => {
            controller.abort();
            setLoading(false);
        }
    }, [ user, url, refreshKey ]);

    return [
        result,
        loading
    ];
};

export default useWorkItemMetadataRequirements;